/**
 * Reusable form field components for cattle registration
 * BUGFIX: Extracted from AddCattle.tsx to prevent component re-creation causing input unfocus
 */

import React, { useState } from 'react';
import { AlertCircle, Upload, X } from 'lucide-react';

export const InputField = ({
  label,
  value,
  onChange,
  type = 'text',
  placeholder,
  required = false,
  error,
  className = '',
  ...props
}: any) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-bold text-gray-900 tracking-wide uppercase text-[11px] mb-1">
      {label} {required && <span className="text-red-600 font-extrabold">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
        error ? 'border-red-300 bg-red-50' : 'border-gray-300'
      }`}
      {...props}
    />
    {error && (
      <div className="flex items-center gap-2 text-sm text-red-600">
        <AlertCircle className="h-4 w-4" />
        {error}
      </div>
    )}
  </div>
);

export const SelectField = ({
  label,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  error,
  className = ''
}: any) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-bold text-gray-900 tracking-wide uppercase text-[11px] mb-1">
      {label} {required && <span className="text-red-600 font-extrabold">*</span>}
    </label>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={`w-full px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 ${
        error ? 'border-red-300 bg-red-50' : 'border-gray-300'
      }`}
    >
      <option value="">{placeholder}</option>
      {options.map((option: any) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    {error && (
      <div className="flex items-center gap-2 text-sm text-red-600">
        <AlertCircle className="h-4 w-4" />
        {error}
      </div>
    )}
  </div>
);

export const TextAreaField = ({
  label,
  value,
  onChange,
  placeholder,
  rows = 3,
  className = '',
  ...props
}: any) => (
  <div className={`space-y-2 ${className}`}>
    <label className="block text-sm font-bold text-gray-900 tracking-wide uppercase text-[11px] mb-1">
      {label}
    </label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      rows={rows}
      className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-400 resize-none"
      {...props}
    />
  </div>
);

export const MultiSelectField = ({
  label,
  value = [],
  onChange,
  options,
  placeholder,
  required = false,
  error,
  className = ''
}: any) => {
  const handleToggle = (optionValue: string) => {
    const currentValues = Array.isArray(value) ? value : [];
    if (currentValues.includes(optionValue)) {
      onChange(currentValues.filter((v: string) => v !== optionValue));
    } else {
      onChange([...currentValues, optionValue]);
    }
  };

  const selectedLabels = options
    .filter((opt: any) => value.includes(opt.value))
    .map((opt: any) => opt.label)
    .join(', ');

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-bold text-gray-900 tracking-wide uppercase text-[11px] mb-1">
        {label} {required && <span className="text-red-600 font-extrabold">*</span>}
      </label>
      <div className="relative">
        <div className={`w-full min-h-[42px] px-3 py-2 bg-white border rounded-lg transition-all duration-200 text-gray-900 focus-within:outline-none focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-transparent hover:border-gray-400 ${
          error ? 'border-red-300 bg-red-50' : 'border-gray-300'
        }`}>
          {value.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {options
                .filter((opt: any) => value.includes(opt.value))
                .map((opt: any) => (
                  <span
                    key={opt.value}
                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
                  >
                    {opt.label}
                    <button
                      type="button"
                      onClick={() => handleToggle(opt.value)}
                      className="hover:bg-blue-200 rounded-full p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
            </div>
          ) : (
            <span className="text-gray-400">{placeholder}</span>
          )}
        </div>
        <div className="mt-2 p-2 bg-white border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
          {options.map((option: any) => (
            <label
              key={option.value}
              className="flex items-center gap-2 px-2 py-2 hover:bg-gray-50 rounded cursor-pointer"
            >
              <input
                type="checkbox"
                checked={value.includes(option.value)}
                onChange={() => handleToggle(option.value)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-gray-900">{option.label}</span>
            </label>
          ))}
        </div>
      </div>
      {error && (
        <div className="flex items-center gap-2 text-sm text-red-600">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      )}
    </div>
  );
};

export const FileUploadField = ({
  label,
  value,
  onChange,
  accept,
  className = '',
  currentImage = null,
  currentDocument = null
}: any) => {
  const [preview, setPreview] = useState<string | null>(null);

  // Generate preview when value changes
  React.useEffect(() => {
    if (value && value instanceof File) {
      // Check if it's an image file
      const isImage = accept?.includes('image') || value.type.startsWith('image/');

      if (isImage) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setPreview(reader.result as string);
        };
        reader.readAsDataURL(value);
      } else {
        setPreview(null);
      }
    } else {
      setPreview(null);
    }
  }, [value, accept]);

  const handleFileChange = (file: File | null) => {
    onChange(file);
  };

  const handleClear = () => {
    handleFileChange(null);
  };

  // Check if we have an existing file (image or document)
  const hasExistingFile = currentImage || currentDocument;
  const existingFileLabel = currentImage ? '(Click to replace)' : currentDocument ? '(Uploaded - Click to replace)' : '';

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="block text-sm font-bold text-gray-900 tracking-wide uppercase text-[11px] mb-1">
        {label} {hasExistingFile && !value && <span className="text-xs font-normal text-blue-600">{existingFileLabel}</span>}
      </label>

      {/* Current Image Display */}
      {currentImage && accept?.includes('image') && !value && (
        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
          <p className="text-xs font-medium text-blue-700 mb-2">📷 Current Image:</p>
          <img
            src={currentImage}
            alt="Current"
            className="w-full h-auto max-h-48 object-contain rounded-lg border border-blue-300 bg-white"
          />
        </div>
      )}

      {/* Current Document Display (for PDFs) */}
      {currentDocument && !accept?.includes('image') && !value && (
        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2">
            <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
              <span className="text-green-600 text-lg">📄</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-green-700">Document Uploaded</p>
              <p className="text-xs text-green-600 truncate">File available on server</p>
            </div>
            <span className="text-green-500 text-lg">✓</span>
          </div>
        </div>
      )}

      <div className="relative">
        <input
          type="file"
          accept={accept}
          onChange={(e) => handleFileChange(e.target.files?.[0] || null)}
          className="hidden"
          id={label.replace(/\s+/g, '-').toLowerCase()}
        />
        <label
          htmlFor={label.replace(/\s+/g, '-').toLowerCase()}
          className="flex items-center justify-center w-full px-3 py-2 bg-white border-2 border-dashed border-gray-300 rounded-lg transition-all duration-200 cursor-pointer hover:border-blue-400 hover:bg-blue-50 group"
        >
          <div className="flex items-center gap-3 text-gray-600 group-hover:text-blue-600">
            <Upload className="h-5 w-5" />
            <span className="text-sm font-medium">
              {value ? value.name : currentImage ? `Replace ${label.toLowerCase()}` : `Choose ${label.toLowerCase()}`}
            </span>
          </div>
        </label>
        {value && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-2 right-2 p-1 bg-red-100 text-red-600 rounded-full hover:bg-red-200 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Image Preview Section */}
      {preview && (
        <div className="mt-3 p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border-2 border-green-300 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <div className="h-6 w-6 rounded-full bg-green-500 flex items-center justify-center">
              <span className="text-white text-sm">✓</span>
            </div>
            <p className="text-sm font-semibold text-green-800">Image Preview</p>
          </div>
          <div className="relative bg-white rounded-lg p-2 border border-green-200">
            <img
              src={preview}
              alt="Preview"
              className="w-full h-auto max-h-64 object-contain rounded"
            />
          </div>
          <p className="text-xs text-green-700 mt-2 text-center">
            Selected: {value?.name} ({(value?.size / 1024).toFixed(1)} KB)
          </p>
        </div>
      )}
    </div>
  );
};
